import React, { useMemo, useState } from 'react';
import useTranslation from '../../../../../Hooks/useTranslation';
import InvoicingCard from '../InvoicingCard';
import InvoicingChart from './InvoicingChart';
// @ts-ignore
import styles from './InvoicingWrapper.module.scss';
import { useOrderStatisticsQuery } from '../../../../../graphql/graphql';
import ScrollContainer from '../../../../Base/Components/ScrollContainer';
import useFilters from '../../../../Base/Hooks/useFilters';
import Loading from '../../../../../Components/Loading';
import moment from 'moment';
import InvoicingTableWrapper from '../InvoicingTable/InvoicingTableWrapper';
import useCurrency from '../../../../Region/Hooks/useCurrency';
import { useAppContext } from '../../../../../Services/AppContext';

interface ChartData {
	total: number[];
	commission: number[];
	labels: string[];
}

function parseNumberFromPrice(price: string) {
	const result = price?.match(/[0-9.]+/);
	return result ? parseFloat(result[0]) : 0;
}

interface Props {
	isSuperAdmin?: boolean;
}

const InvoicingWrapper: React.FC<Props> = ({ isSuperAdmin }) => {
	const { handleDateTimeValue } = useFilters();
	const { t } = useTranslation();
	const { withCurrencyCode } = useCurrency();
	const { workplaceId } = useAppContext();
	const [totalInvoiced, setTotalInvoiced] = useState<string>('$0');
	const [totalCommission, setTotalCommission] = useState<string>('$0');
	const { data: statisticsData, isLoading: isLoadingStatistics } =
		useOrderStatisticsQuery({
			where: {
				from: handleDateTimeValue(
					moment().subtract(12, 'months').startOf('month'),
				),
				to: handleDateTimeValue(moment().endOf('month')),
				sendEmpty: true,
				isAdmin: isSuperAdmin,
				workplaceId,
			},
		});

	const chartData: ChartData = useMemo(() => {
		const total: number[] = [];
		const commission: number[] = [];
		const labels: string[] = [];
		let tempTotalCommission: number = 0;
		let tempTotalInvoiced: number = 0;

		statisticsData?.orderStatistics?.data?.forEach(item => {
			tempTotalInvoiced += item?.totalInvoiced ?? 0;
			total.push(item?.totalInvoiced ?? 0);
			tempTotalCommission += item?.totalCommission ?? 0;
			commission.push(item?.totalCommission ?? 0);
			if (item?.label) {
				labels.push(item.label);
			}
		});

		setTotalInvoiced(withCurrencyCode(tempTotalInvoiced.toFixed(2)));
		setTotalCommission(withCurrencyCode(tempTotalCommission.toFixed(2)));

		return {
			total,
			commission,
			labels,
		};
	}, [statisticsData]);

	if (isLoadingStatistics) {
		return <Loading />;
	}

	return (
		<ScrollContainer>
			<div className='px-4' style={{ height: '350px' }}>
				<InvoicingChart
					datasets={[
						{
							label: t('component.invoicing.commission'),
							data: chartData.commission,
							backgroundColor: function (context: any) {
								const ctx = context.chart.ctx;
								const gradient = ctx.createLinearGradient(0, 0, 0, 900);
								gradient.addColorStop(0, '#9ac729');
								gradient.addColorStop(1, '#619612');
								return gradient;
							},
						},
						{
							label: t('component.invoicing.totalInvoiced'),
							data: chartData.total,
							backgroundColor: 'rgba(216,220,228, 0.5)',
						},
					]}
					labels={chartData.labels}
				/>
			</div>
			<div className={styles.invoicingDetails}>
				<InvoicingCard
					variant='commission'
					label={t('component.invoicing.commissionLabel')!}
					price={totalCommission}
				/>
				<InvoicingCard
					variant='invoiced'
					label={t('component.invoicing.totalInvoiced')!}
					price={totalInvoiced}
				/>
			</div>
			<InvoicingTableWrapper isSuperAdmin={isSuperAdmin} />
		</ScrollContainer>
	);
};

export default InvoicingWrapper;
